import { useEffect, useState } from 'react';

/**
 * Reports which of the given section ids is currently the "active" one, so the
 * nav can highlight it. Uses IntersectionObserver rather than scroll handlers.
 */
export function useScrollSpy(ids: readonly string[], topOffset = 96): string | null {
  const [activeId, setActiveId] = useState<string | null>(null);

  useEffect(() => {
    const elements = ids
      .map((id) => document.getElementById(id))
      .filter((element): element is HTMLElement => element !== null);

    if (elements.length === 0) return;

    const visible = new Set<string>();

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) visible.add(entry.target.id);
          else visible.delete(entry.target.id);
        }
        // Document order wins, so the topmost visible section is the active one.
        const next = ids.find((id) => visible.has(id)) ?? null;
        setActiveId((current) => next ?? current);
      },
      { rootMargin: `-${topOffset}px 0px -55% 0px`, threshold: 0 },
    );

    elements.forEach((element) => observer.observe(element));
    return () => observer.disconnect();
  }, [ids, topOffset]);

  return activeId;
}
